import { useAuth, useCachedFetch } from '@structural-climbing/auth'

export interface SessionTop {
  routeId: string
  loggedAt: string
  color: string | null
  grade: number | null
  imageR2Key: string | null
  sectionName: string | null
  discipline: string | null
  flash: boolean
  /** Topped for the first time today. Missing from an older Worker: assume so. */
  firstTop?: boolean
}

export interface MySession {
  todayStart: string
  /** Everything logged today at this gym, newest first. */
  tops: SessionTop[]
  /** Today's latest own visit, with its check-out once it ended. */
  visit: { checkedInAt: string; checkedOutAt: string | null } | null
  /** When the visit before today began. */
  lastVisitAt: string | null
}

export const MY_SESSION_CACHE_PREFIX = 'my-session:'

/**
 * The member's day at this gym: what they topped, when they came and left, and
 * when they were last here. One request feeds the session card, the recap and
 * "new since your last visit".
 *
 * Cleared by `invalidateAfterAscent` when a top is logged, and by Home when the
 * desk checks the member in or out.
 */
export function useMySession(gymId: string | null) {
  const { isAuthenticated, getAccessTokenSilently } = useAuth()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const { data } = useCachedFetch<MySession>(
    isAuthenticated && gymId ? `${MY_SESSION_CACHE_PREFIX}${gymId}` : null,
    async () => {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/my-session?gym_id=${encodeURIComponent(gymId!)}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error('Failed to load the session')
      return (await res.json()) as MySession
    },
  )

  return data ?? null
}
